import * as t from 'io-ts';

import {ApplicationCancelToken, HttpVerbsEnum} from '../../applications/bridge/v2/types/bridgeTypesV2';
import {applicationConversationStatusMap} from '../../store/data/applications/applicationConversationsActions';
import {makeEnumRuntimeType} from '../api/apiTypes';
import {RApplicationPaginationToken} from './applicationPaginationTokenTypes';
import {makePublicIdType} from './publicIdTypes';
import {makePublicUidType} from './publicUidTypes';
import {RWidgetTemplate} from './widgetTemplateModel';

/*
 * Runtime types.
 */

const RDraftContent = t.strict({
  body: t.string,
  type: t.union([t.literal('html'), t.literal('text')]),
});

const RDraftBase = t.partial({
  to: t.readonlyArray(t.string),
  cc: t.readonlyArray(t.string),
  bcc: t.readonlyArray(t.string),
  subject: t.string,
  content: RDraftContent,
  // Files are not a type in io-ts
  attachments: t.readonlyArray(t.any),
});

const RDraftTemplate = t.exact(
  t.intersection([
    RDraftBase,
    t.partial({
      channelId: makePublicIdType('channel'),
      replyOptions: t.strict({
        type: t.union([t.literal('forward'), t.literal('reply'), t.literal('replyAll')]),
        originalMessageId: makePublicIdType('message'),
      }),
    }),
  ]),
);

const RDraftUpdate = t.exact(
  t.intersection([
    RDraftBase,
    t.type({
      updateMode: t.union([t.literal('insert'), t.literal('replace')]),
    }),
  ]),
);

const RHttpRequest = t.exact(
  t.intersection([
    t.type({
      verb: makeEnumRuntimeType(HttpVerbsEnum),
      path: t.string,
    }),
    t.partial({
      body: t.unknown,
    }),
  ]),
);

const RHttpRelayRequest = t.exact(
  t.intersection([
    t.type({
      verb: makeEnumRuntimeType(HttpVerbsEnum),
      url: t.string,
    }),
    t.partial({
      headers: t.record(t.string, t.string),
      body: t.unknown,
    }),
  ]),
);

const ROpenUrlInPopupOptions = t.partial({
  width: t.Integer,
  height: t.Integer,
});

/*
 * Context functions runtime types.
 */

// This is only for type-checking purposes.
// We may be able to remove this one day if TypeScript supports adding to generic tuples.
const RFakeCancelToken = t.union([t.clean<ApplicationCancelToken>(t.any), t.undefined]);

const RApplicationContextBaseFunctions = t.partial({
  createWidget: t.tuple([RWidgetTemplate, RFakeCancelToken]),
  destroyWidget: t.tuple([t.string, RFakeCancelToken]),
  sendHttp: t.tuple([RHttpRequest, RFakeCancelToken]),
  relayHttp: t.tuple([RHttpRelayRequest, RFakeCancelToken]),
  authenticate: t.tuple([RFakeCancelToken]),
  deauthenticate: t.tuple([RFakeCancelToken]),
  openUrl: t.tuple([t.string, RFakeCancelToken]),
  openUrlInPopup: t.tuple([t.string, t.union([ROpenUrlInPopupOptions, t.undefined]), RFakeCancelToken]),
  search: t.tuple([t.string, RFakeCancelToken]),
  listChannels: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  listInboxes: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  listTags: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  listTeammates: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  createDraft: t.tuple([RDraftTemplate, RFakeCancelToken]),
  updateDraft: t.tuple([makePublicUidType('draft'), RDraftUpdate, RFakeCancelToken]),
});

const RApplicationContextConversationsFunctions = t.partial({
  /** @deprecated */
  addTopic: t.tuple([t.string, t.union([t.string, t.undefined]), RFakeCancelToken]),
  addLink: t.tuple([t.string, t.union([t.string, t.undefined]), RFakeCancelToken]),
  assign: t.tuple([t.union([makePublicIdType('teammate'), t.null]), RFakeCancelToken]),
  move: t.tuple([makePublicIdType('inbox'), RFakeCancelToken]),
  setStatus: t.tuple([t.keyof(applicationConversationStatusMap), RFakeCancelToken]),
  tag: t.tuple([t.readonlyArray(makePublicIdType('tag')), RFakeCancelToken]),
  untag: t.tuple([t.readonlyArray(makePublicIdType('tag')), RFakeCancelToken]),
  fetchDraft: t.tuple([makePublicUidType('draft'), RFakeCancelToken]),
  listComments: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  listMessages: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  listRecipients: t.tuple([t.union([RApplicationPaginationToken, t.undefined]), RFakeCancelToken]),
  downloadAttachment: t.tuple([
    t.union([makePublicIdType('message'), makePublicIdType('comment')]),
    makePublicIdType('attachment'),
    RFakeCancelToken,
  ]),
  removeLink: t.tuple([makePublicIdType('topic'), RFakeCancelToken]),
});

export const RApplicationContextFunctions = t.exact(
  t.intersection([RApplicationContextBaseFunctions, RApplicationContextConversationsFunctions]),
);
